import { useState } from "react";
import { ChevronDown, Minus, Plus, Trash2, AlertCircle } from "lucide-react";
import { fmtINR } from "@/lib/portalApi";
import { FREQUENCIES, UOMS } from "@/lib/plannerApi";

export const ListItemRow = ({ it, idx, isBiz, departments, onChange, onRemove }) => {
    const [open, setOpen] = useState(false);
    const set = (k, v) => onChange({ ...it, [k]: v });
    const unresolved = !it.item_id;
    return (
        <div className={`rounded-xl border p-3 ${it.skip_this_month ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`}
             data-testid={`planner-item-${idx}`}>
            <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate" title={it.name}>{it.name}</div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                        {unresolved ? (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-bold" data-testid={`planner-item-unresolved-${idx}`}>
                                <AlertCircle size={10}/> We'll confirm this item
                            </span>
                        ) : it.rate ? <span className="font-mono">{fmtINR(it.rate)} each</span> : null}
                        {it.brand_preference && <span>Brand: {it.brand_preference}</span>}
                        {it.pack_size && <span>{it.pack_size} {it.uom}</span>}
                        {isBiz && it.department && <span>· {it.department}</span>}
                        {it.skip_this_month && <span className="font-bold">Skipped this month</span>}
                    </div>
                </div>
                <div className="flex items-center rounded-lg border border-slate-200">
                    <button onClick={() => set("qty", Math.max(0, (it.qty || 0) - 1))} className="p-1.5 text-slate-500 hover:text-slate-900" data-testid={`planner-item-dec-${idx}`}><Minus size={12}/></button>
                    <input value={it.qty} inputMode="decimal" onChange={(e) => set("qty", e.target.value === "" ? 0 : Number(e.target.value) || 0)}
                           className="w-10 text-center text-sm font-mono outline-none" data-testid={`planner-item-qty-${idx}`}/>
                    <button onClick={() => set("qty", (it.qty || 0) + 1)} className="p-1.5 text-slate-500 hover:text-slate-900" data-testid={`planner-item-inc-${idx}`}><Plus size={12}/></button>
                </div>
                <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Details" data-testid={`planner-item-details-${idx}`}>
                    <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
                </button>
                <button onClick={onRemove} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600" title="Remove" data-testid={`planner-item-remove-${idx}`}>
                    <Trash2 size={14}/>
                </button>
            </div>
            {open && (
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100 text-xs">
                    <In label="Preferred brand" v={it.brand_preference} on={(v) => set("brand_preference", v)} tid={`planner-item-brand-${idx}`}/>
                    <In label="Alternative brands" v={it.alt_brands} on={(v) => set("alt_brands", v)} tid={`planner-item-alt-${idx}`}/>
                    <In label="Pack size" v={it.pack_size} on={(v) => set("pack_size", v)} tid={`planner-item-pack-${idx}`} ph="e.g. 5"/>
                    <label className="block"><span className="hdb-label">Unit</span>
                        <select className="hdb-input" value={it.uom} onChange={(e) => set("uom", e.target.value)} data-testid={`planner-item-uom-${idx}`}>
                            {UOMS.map((u) => <option key={u} value={u}>{u || "—"}</option>)}
                        </select>
                    </label>
                    <label className="block"><span className="hdb-label">How often</span>
                        <select className="hdb-input" value={it.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid={`planner-item-freq-${idx}`}>
                            {FREQUENCIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </label>
                    {isBiz && (
                        <label className="block"><span className="hdb-label">Department</span>
                            <select className="hdb-input" value={it.department} onChange={(e) => set("department", e.target.value)} data-testid={`planner-item-dept-${idx}`}>
                                <option value="">—</option>
                                {departments.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                            </select>
                        </label>
                    )}
                    <In label="Notes" v={it.notes} on={(v) => set("notes", v)} tid={`planner-item-notes-${idx}`} wide/>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={it.substitution_allowed} onChange={(e) => set("substitution_allowed", e.target.checked)} data-testid={`planner-item-subs-${idx}`}/> Substitutes OK</label>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={it.skip_this_month} onChange={(e) => set("skip_this_month", e.target.checked)} data-testid={`planner-item-skip-${idx}`}/> Skip this month</label>
                </div>
            )}
        </div>
    );
};

const In = ({ label, v, on, tid, ph = "", wide = false }) => (
    <label className={`block ${wide ? "col-span-2" : ""}`}><span className="hdb-label">{label}</span>
        <input className="hdb-input" value={v || ""} placeholder={ph} onChange={(e) => on(e.target.value)} data-testid={tid}/>
    </label>
);
